import { useEffect } from "react";
import { Link } from "react-router-dom";
import { centsLabel, type ShopProductSummary } from "@gooner/shared";
import { track } from "../../analytics";

/**
 * The house card's shop variant: one product, its photo, its price and a way in. Rendered
 * inside HouseCard's own box, so the box, the layout choice and the no-"Advertisement" rule
 * stay with HouseCard; this is only what goes in it.
 */
export function ShopPromoCard({
  product,
  compact,
  reason,
  panel,
  fill = false,
}: {
  product: ShopProductSummary;
  compact: boolean;
  /** Fill a unit of this size: the photo large, the name, price and button under it. */
  panel?: { width: number; height: number };
  /** Fill the parent cell: the photo on the left at the clips' 4:5, the words on the right. */
  fill?: boolean;
  /** rotation: the shop's half of the slots. ad_fallback: an ad was asked for and didn't fill.
   *  member: a Premium member's slot, which only ever shows the shop. */
  reason: "rotation" | "ad_fallback" | "member";
}) {
  const go = () => track("shop_promo_clicked", { slug: product.slug, placement: "house_card", reason });
  useEffect(() => {
    track("shop_promo_shown", { slug: product.slug, placement: "house_card", reason });
  }, [product.slug, reason]);
  const cta = (
    <Link
      to={`/shop/${product.slug}`}
      onClick={go}
      className={`shrink-0 rounded-lg bg-[#f5c518] py-1.5 text-center font-semibold text-black hover:brightness-110 ${compact ? "w-full px-2 text-[13px] leading-tight" : "px-3 text-sm whitespace-nowrap"}`}
    >
      Shop now
    </Link>
  );
  const photo = product.images[0] ? (
    <Link to={`/shop/${product.slug}`} onClick={go} className="shrink-0 overflow-hidden rounded-md bg-[var(--color-panel-2)]">
      <img src={product.images[0]} alt="" loading="lazy" className={compact ? "h-28 w-24 object-cover" : "h-16 w-14 object-cover"} />
    </Link>
  ) : null;
  if (panel && fill) {
    return (
      <Link
        to={`/shop/${product.slug}`}
        onClick={go}
        // From sm up the cell shares its row with a tile, so the card pins to the cell's edges
        // and the row keeps the tiles' height. On a two-column phone grid the cell is the whole
        // row, with nothing beside it to set a height, so the card brings its own.
        className="group flex h-[270px] w-full overflow-hidden rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] text-left sm:absolute sm:inset-0 sm:h-auto"
      >
        <div className="w-[48%] shrink-0 overflow-hidden bg-[var(--color-panel-2)]">
          {product.images[0] ? (
            <img src={product.images[0]} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
          ) : null}
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5 p-4">
          <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-accent-soft)]">GoonLords shop</p>
          <p className="text-base font-semibold leading-snug text-zinc-100 sm:text-lg">{product.name}</p>
          {product.tagline ? <p className="line-clamp-3 text-xs text-zinc-400 sm:text-sm">{product.tagline}</p> : null}
          <p className="text-sm font-semibold text-zinc-100">{centsLabel(product.priceCents)}</p>
          <span className="mt-1 self-start rounded-lg bg-[#f5c518] px-3 py-1.5 text-sm font-semibold text-black group-hover:brightness-110">
            Shop now
          </span>
        </div>
      </Link>
    );
  }
  if (panel) {
    return (
      <Link
        to={`/shop/${product.slug}`}
        onClick={go}
        className="group flex flex-col overflow-hidden rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] text-left"
        style={{ width: panel.width, height: panel.height, maxWidth: "100%" }}
      >
        <div className="min-h-0 flex-1 overflow-hidden bg-[var(--color-panel-2)]">
          {product.images[0] ? (
            <img src={product.images[0]} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
          ) : null}
        </div>
        <div className={`flex shrink-0 gap-2 p-2.5 ${compact ? "flex-col" : "items-center justify-between"}`}>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-zinc-100">{product.name}</p>
            <p className="text-xs text-zinc-400">{centsLabel(product.priceCents)} in the GoonLords shop</p>
          </div>
          <span className={`shrink-0 rounded-lg bg-[#f5c518] py-1.5 text-center font-semibold text-black group-hover:brightness-110 ${compact ? "w-full px-2 text-[13px]" : "px-3 text-sm"}`}>
            Shop now
          </span>
        </div>
      </Link>
    );
  }
  if (compact) {
    return (
      <>
        {photo}
        <p className="text-sm font-semibold text-zinc-100">{product.name}</p>
        <p className="text-xs text-zinc-400">{centsLabel(product.priceCents)} in the GoonLords shop</p>
        {cta}
      </>
    );
  }
  return (
    <>
      {photo}
      <div className="min-w-[8rem] flex-1">
        <p className="text-sm font-semibold text-zinc-100">{product.name}</p>
        <p className="mt-0.5 text-xs text-zinc-400">{centsLabel(product.priceCents)} in the GoonLords shop</p>
      </div>
      {cta}
    </>
  );
}
